import React from 'react';
import { useSelector } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';
import styled from '@emotion/styled';
import * as Yup from 'yup';

import { showSuccessToast } from '../utils/toastManager';
import iconSet from '../images/teambuildr-selection.json';
import useLayout from './Layout/hooks/useLayout';
import Button from '../_TBComponents/Button';
import Text from '../_TBComponents/Text';
import Modal from '../_TBComponents/Modal';

const FieldError = styled('div')`
  font-size: 14px;
  color: #ff6600;
`;

const TitlePlusErrorWrapper = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NavigationWrapperFolder = styled('div')`
  position: absolute;
  display: flex;
  justify-content: flex-end;
  height: 20px;
  width: 20px;
  z-index: 1;
  top: 15px;
  right: 25px;
`;

const FormGroup = styled('div')`
  label {
    width: 100%;
  }
  button {
    margin-bottom: 20px;
  }
  .easy-join {
    margin-bottom: 15px;
  }

  z-index: 1;
  @media screen and (max-width: 600px) {
    a {
      font-size: 11px;
    }
  }
  .form-control {
    min-height: 30px;
    width: 100%;
  }
`;

export const ModalContentContainer = styled('div')`
  min-width: 100%;
  padding: 0px 40px 15px 40px;
`;

const supportSchema = Yup.object().shape({
  subject: Yup.string().required('Subject is required'),
  body: Yup.string().required('Message is required'),
});

const ModalSupportNew = ({
  path, isOpen, onRequestClose, initialSubject,
}) => {
  const { handleSupportSubmission } = useLayout();
  const isSupportSubmitting = useSelector(
    (state) => state.settings.ui.isSupportSubmitting,
  );

  const handleSubmitForm = (values) => {
    handleSupportSubmission(values);
    showSuccessToast('Support message sent successfully!');
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      title='Support'
      titleIcon='question-mark'
      customBorderRadius='12px'
    >
      <NavigationWrapperFolder>
        <Text
          styledWrapperProps={{ cursor: 'pointer' }}
          onClick={() => {
            onRequestClose();
          }}
        >
          <IcomoonReact iconSet={iconSet} size={10} icon='remove' />
        </Text>
      </NavigationWrapperFolder>
      <ModalContentContainer>
        <Formik
          validationSchema={supportSchema}
          initialValues={{
            subject: initialSubject,
            body: '',
            page: path,
          }}
          onSubmit={(values, { setSubmitting }) => {
            handleSubmitForm(values);
            setSubmitting(false);
          }}
        >
          {({
            errors, touched, handleSubmit, handleChange, values,
          }) => (
            <Form>
              <Text styledTextProps={{ fontSize: '19px', fontWeight: '600' }}>
                Have a question or suggestion? Fill out the form below and send
                it to us!
              </Text>
              <Text
                styledTextProps={{ fontSize: '16px' }}
                styledWrapperProps={{ margin: '15px 0' }}
              >
                Be sure to check out our&nbsp;
                <a
                  href='https://support.teambuildr.com/'
                  rel='noopener noreferrer'
                  target='_blank'
                  style={{
                    /* Global anchors are near-black, invisible on the dark modal;
                       follow the surrounding Text ink instead. */
                    color: 'inherit',
                    textDecoration: 'underline',
                  }}
                >
                  Knowledge Base
                </a>
                &nbsp;for some quick tips and walkthroughs.
              </Text>
              <Text
                styledTextProps={{ fontSize: '16px' }}
                styledWrapperProps={{ margin: '15px 0' }}
              >
                Need more assistance?&nbsp;
                <a
                  href='https://calendly.com/tb-support'
                  rel='noopener noreferrer'
                  target='_blank'
                  style={{
                    /* Global anchors are near-black, invisible on the dark modal;
                       follow the surrounding Text ink instead. */
                    color: 'inherit',
                    textDecoration: 'underline',
                  }}
                >
                  Book a call
                </a>
                &nbsp;with our support team, or contact us directly at
                240-696-1395.
              </Text>
              <FormGroup className='form-group'>
                <TitlePlusErrorWrapper>
                  <Text
                    styledTextProps={{ fontSize: '16px', fontWeight: '700' }}
                    styledWrapperProps={{ marginBottom: '5px' }}
                  >
                    Subject
                  </Text>
                  {errors.subject && touched.subject ? (
                    <FieldError className='error-text'>
                      {errors.subject}
                    </FieldError>
                  ) : null}
                </TitlePlusErrorWrapper>
                <Field
                  className='form-control'
                  id='subject-field'
                  name='subject'
                  placeholder='Support Question / Suggestion Subject'
                />
              </FormGroup>
              <FormGroup className='form-group'>
                <TitlePlusErrorWrapper>
                  <Text
                    styledTextProps={{ fontSize: '16px', fontWeight: '700' }}
                    styledWrapperProps={{ marginBottom: '5px' }}
                  >
                    Message
                  </Text>
                  {errors.body && touched.body ? (
                    <FieldError className='error-text'>
                      {errors.body}
                    </FieldError>
                  ) : null}
                </TitlePlusErrorWrapper>
                <Field
                  onChange={handleChange}
                  name='body'
                  component='textarea'
                  className='form-control'
                  placeholder='Describe your problem or suggestion here...'
                  value={values.body}
                />
              </FormGroup>
              <Button
                name={!isSupportSubmitting ? 'Submit' : 'Submitting...'}
                bgColor='#0DCC8A'
                fullWidth
                large
                noBorder
                bottom
                fontSize={14}
                txtColor='#FFFFFF'
                upperCase
                customBorderRadius='0 0 12px 12px'
                styledWrapperProps={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                }}
                disabled={isSupportSubmitting}
                onClick={handleSubmit}
              />
            </Form>
          )}
        </Formik>
      </ModalContentContainer>
    </Modal>
  );
};

ModalSupportNew.propTypes = {
  path: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  initialSubject: PropTypes.string,
};

ModalSupportNew.defaultProps = {
  initialSubject: '',
};

export default ModalSupportNew;
